import React, { useEffect, useState } from "react";
import styled from "styled-components";
import ResponsiveBox from "./ResponsiveBox";
import {
  ChevronUp,
  ChevronDown,
  HelpCircle,
  X,
  Info,
  User,
  Maximize2,
  Edit2,
  Trash2,
} from "react-feather";
import Avatar from "./Avatar";
import { rawColors } from "../design-system/tokens/colors";

const AnnotationsListWrapper = styled(ResponsiveBox)<{ $isOpen: boolean }>`
  position: absolute;
  top: 70px;
  left: 76px;
  width: calc(25% - 25px);
  height: calc(100vh - 88px);
  transform: translateX(${({ $isOpen }) => ($isOpen ? "0" : "100%")});
  opacity: ${({ $isOpen }) => ($isOpen ? "1" : "0")};
  visibility: ${({ $isOpen }) => ($isOpen ? "visible" : "hidden")};
  transition: transform 0.3s ease-in-out, opacity 0.3s ease-in-out,
    visibility 0.3s ease-in-out;
  pointer-events: ${({ $isOpen }) => ($isOpen ? "auto" : "none")};
  display: flex;
  flex-direction: column;
  box-shadow: 0 0 10px rgba(0, 0, 0, 0.1);
  padding: 0;

  &::before {
    height: 0px;
  }
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 15px;
`;

const HeaderTitle = styled.span`
  margin: 0;
  font-size: 18px;
  font-weight: 500;
`;

const HeaderIcons = styled.div`
  display: flex;
  gap: 10px;
`;

const ScrollableArea = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 15px;

  &::-webkit-scrollbar {
    width: 8px;
  }

  &::-webkit-scrollbar-track {
    background: #f1f1f1;
  }

  &::-webkit-scrollbar-thumb {
    background: #888;
    border-radius: 4px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: #555;
  }
`;

const Footer = styled.div`
  display: flex;
  align-items: center;
  padding: 15px;
  border-top: 1px solid ${rawColors.grey[200]};
  color: ${rawColors.text.mediumEmphasis};
`;

const AnnotationBox = styled.div<{ $isExpanded: boolean }>`
  border: 1px solid ${rawColors.grey[200]};
  border-radius: 8px;
  margin-bottom: 15px;
  overflow: hidden;
  background-color: ${rawColors.structural.white};
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
`;

const AnnotationHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 15px;
  cursor: pointer;
`;

const AnnotationTitle = styled.span`
  //font-weight: bold;
  font-size: 14px;
  font-weight: 500;
`;

const MetadataBox = styled.div`
  border: 1px solid ${rawColors.grey[200]};
  border-radius: 4px;
  padding: 5px 10px;
  margin: 10px 15px;
  font-size: 12px;
`;

const MetadataLabel = styled.span`
  color: ${rawColors.text.mediumEmphasis};
`;

const MetadataValue = styled.span`
  color: ${rawColors.text.highEmphasis};
  margin-left: 5px;
  font-weight: 500;
`;

const AnnotationContent = styled.div`
  padding: 0 15px 15px;
  font-size: 12px;
  color: ${rawColors.text.mediumEmphasis};
  padding-bottom: 0;
`;

const AuthorInfo = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 10px;
  font-size: 12px;
  color: ${rawColors.text.mediumEmphasis};
  gap: 10px;
`;

const Separator = styled.span`
  width: 1px;
  height: 28px;
  background-color: #e0e0e0;
  margin: 0 10px;
`;

const Description = styled.p`
  margin: 0 0 15px 0;
`;

const Divider = styled.hr`
  border: none;
  border-top: 1px solid ${rawColors.grey[200]};
  margin: 10px -15px;
  margin-bottom: 0px;
`;

const ActionBar = styled.div`
  display: flex;
  justify-content: space-between;
`;

const ActionButton = styled.button`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  background: none;
  border: none;
  padding: 10px;
  cursor: pointer;
  color: ${rawColors.text.mediumEmphasis};

  &:not(:last-child) {
    border-right: 1px solid ${rawColors.grey[200]};
  }

  &:hover {
    background-color: #f0f0f0;
  }
`;

interface AnnotationsListProps {
  isOpen: boolean;
  onClose: () => void;
  isBuildMode: boolean;
}

const AnnotationsList: React.FC<AnnotationsListProps> = ({
  isOpen,
  onClose,
  isBuildMode,
}) => {
  const [expandedAnnotations, setExpandedAnnotations] = useState<Set<number>>(
    new Set()
  );

  useEffect(() => {
    if (!isBuildMode) {
      onClose();
    }
  }, [isBuildMode, onClose]);

  const toggleAnnotation = (id: number) => {
    setExpandedAnnotations((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(id)) {
        newSet.delete(id);
      } else {
        newSet.add(id);
      }
      return newSet;
    });
  };

  const getRandomColor = () => {
    const hue = Math.floor(Math.random() * 360);
    const saturation = Math.floor(Math.random() * 30) + 70;
    const lightness = Math.floor(Math.random() * 20) + 20;

    const color = `hsl(${hue}, ${saturation}%, ${lightness}%)`;
    const lighterColor = `hsl(${hue}, ${saturation}%, ${Math.min(
      lightness * 3,
      90
    )}%)`;

    // Convert HSL to HEX
    const toHex = (c: number) => {
      const hex = c.toString(16);
      return hex.length === 1 ? "0" + hex : hex;
    };

    const rgbToHex = (r: number, g: number, b: number) =>
      "#" + toHex(r) + toHex(g) + toHex(b);

    const hslToRgb = (h: number, s: number, l: number) => {
      h /= 360;
      s /= 100;
      l /= 100;
      let r, g, b;
      if (s === 0) {
        r = g = b = l;
      } else {
        const hue2rgb = (p: number, q: number, t: number) => {
          if (t < 0) t += 1;
          if (t > 1) t -= 1;
          if (t < 1 / 6) return p + (q - p) * 6 * t;
          if (t < 1 / 2) return q;
          if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
          return p;
        };
        const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
        const p = 2 * l - q;
        r = hue2rgb(p, q, h + 1 / 3);
        g = hue2rgb(p, q, h);
        b = hue2rgb(p, q, h - 1 / 3);
      }
      return [Math.round(r * 255), Math.round(g * 255), Math.round(b * 255)];
    };

    const [r, g, b] = hslToRgb(hue, saturation, lightness);
    const hexColor = rgbToHex(r, g, b);

    return { color, lighterColor, hexColor };
  };

  const annotations = [
    {
      id: 1,
      title: "July Revenue Spike",
      metadata: { "Data Point": "Revenue: $1.2M" },
      author: "Andy",
      time: "50m ago",
      description:
        "There was a significant spike in revenue during July due to our summer promotion campaign.",
    },
    {
      id: 2,
      title: "Q3 Sales Dip",
      metadata: { "Data Point": "Sales: -15%" },
      author: "Sarah",
      time: "2h ago",
      description:
        "Sales experienced a 15% decrease in Q3, potentially due to increased competition and market saturation.",
    },
    {
      id: 3,
      title: "New Product Launch Impact",
      metadata: { "Data Point": "Market Share: +5%" },
      author: "Mike",
      time: "1d ago",
      description:
        "The launch of our new product line resulted in a 5% increase in market share within the first month.",
    },
    {
      id: 4,
      title: "Customer Satisfaction Peak",
      metadata: { "Data Point": "CSAT Score: 9.2/10" },
      author: "Emily",
      time: "3d ago",
      description:
        "Customer satisfaction reached an all-time high in August, with a CSAT score of 9.2 out of 10.",
    },
    {
      id: 5,
      title: "Website Traffic Surge",
      metadata: { "Data Point": "Visitors: +200%" },
      author: "Alex",
      time: "1w ago",
      description:
        "Our website experienced a 200% increase in traffic following the viral social media campaign.",
    },
    {
      id: 6,
      title: "Operational Cost Reduction",
      metadata: { "Data Point": "Costs: -12%" },
      author: "Lisa",
      time: "2w ago",
      description:
        "Implementation of new automation processes led to a 12% reduction in operational costs.",
    },
    {
      id: 7,
      title: "Employee Turnover Improvement",
      metadata: { "Data Point": "Turnover Rate: 5%" },
      author: "David",
      time: "3w ago",
      description:
        "Employee turnover rate dropped to 5% this quarter, likely due to new retention initiatives.",
    },
  ];

  return (
    <AnnotationsListWrapper $isOpen={isOpen}>
      <Header>
        <HeaderTitle>Add annotations</HeaderTitle>
        <HeaderIcons>
          <HelpCircle size={20} />
          <X size={20} onClick={onClose} style={{ cursor: "pointer" }} />
        </HeaderIcons>
      </Header>
      <ScrollableArea>
        {annotations.map((annotation) => (
          <AnnotationBox
            key={annotation.id}
            $isExpanded={expandedAnnotations.has(annotation.id)}
          >
            <AnnotationHeader onClick={() => toggleAnnotation(annotation.id)}>
              <AnnotationTitle>{annotation.title}</AnnotationTitle>
              {expandedAnnotations.has(annotation.id) ? (
                <ChevronUp size={20} />
              ) : (
                <ChevronDown size={20} />
              )}
            </AnnotationHeader>
            <MetadataBox>
              {Object.entries(annotation.metadata).map(([key, value]) => (
                <React.Fragment key={key}>
                  <MetadataLabel>{key}:</MetadataLabel>
                  <MetadataValue>{value}</MetadataValue>
                </React.Fragment>
              ))}
            </MetadataBox>
            {expandedAnnotations.has(annotation.id) && (
              <AnnotationContent>
                <AuthorInfo>
                  <span>Added by: </span>
                  <Avatar
                    name={annotation.author}
                    color={getRandomColor().hexColor}
                  />
                  <span
                    style={{
                      color: rawColors.text.highEmphasis,
                      fontSize: "12px",
                      fontWeight: "400",
                    }}
                  >
                    {annotation.author}
                  </span>
                  <Separator />
                  <span>{annotation.time}</span>
                </AuthorInfo>
                <Description>{annotation.description}</Description>
                <Divider />
                <ActionBar>
                  <ActionButton>
                    <Maximize2 size={16} />
                  </ActionButton>
                  <ActionButton>
                    <Edit2 size={16} />
                  </ActionButton>
                  <ActionButton>
                    <Trash2 size={16} />
                  </ActionButton>
                </ActionBar>
              </AnnotationContent>
            )}
          </AnnotationBox>
        ))}
      </ScrollableArea>
      <Footer>
        <Info size={16} style={{ marginRight: "10px" }} />
        Select block / data point to annotate
      </Footer>
    </AnnotationsListWrapper>
  );
};

export default AnnotationsList;
